import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { PHASE_EXPERIENCE, phaseForPath } from './phase';
import { ProjectLifecycle } from '../../components/projects/workspace/useProjectLifecycle';
import ProjectActivityRail from '../../components/projects/workspace/ProjectActivityRail';
import PhaseContextNav from '../../components/projects/workspace/PhaseContextNav';
import BaselineLockButton from '../../components/projects/workspace/BaselineLockButton';
import {
  detectActivityFromPath,
  PROJECT_ACTIVITIES,
} from '../../components/projects/workspace/projectActivities';
import '../../components/projects/workspace/workflow-aurora.css';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const GLYPHS = '!<>-_\\/[]{}=+*^?#01ABCDEF';

/** Phase verb that decodes from random glyphs left → right whenever it changes. */
const ScrambleVerb: React.FC<{ text: string }> = ({ text }) => {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return undefined;
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const k = Math.min(1, (t - start) / 700);
      const settled = Math.floor(k * text.length);
      let html = text.slice(0, settled);
      for (let i = settled; i < text.length; i++) {
        html += `<span class="g">${GLYPHS[(Math.random() * GLYPHS.length) | 0]}</span>`;
      }
      el.innerHTML = html;
      if (k < 1) raf = requestAnimationFrame(tick);
      else el.textContent = text;
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      el.textContent = text;
    };
  }, [text]);
  return (
    <span className="verb" ref={ref} aria-label={text}>
      {text}
    </span>
  );
};

type Net = { pulse: (x: number, y: number, big?: boolean) => void };

/** Drifting particle constellation in the active phase colour; repels the cursor, ripples on pulse. */
function useConstellation(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  heroRef: React.RefObject<HTMLElement>,
  colorProbeRef: React.RefObject<HTMLElement>,
): React.MutableRefObject<Net> {
  const api = useRef<Net>({ pulse: () => {} });
  useEffect(() => {
    const cv = canvasRef.current;
    const hero = heroRef.current;
    const ctx = cv?.getContext('2d');
    if (!cv || !hero || !ctx || prefersReducedMotion()) return undefined;
    type P = { x: number; y: number; hx: number; hy: number; ha: number; hs: number; vx: number; vy: number; r: number; e: number };
    let W = 0;
    let H = 0;
    let pts: P[] = [];
    let waves: { x: number; y: number; r: number; big: boolean }[] = [];
    const mouse = { x: -999, y: -999 };
    let col = [120, 120, 255];
    let raf = 0;
    let frameN = 0;

    const resize = () => {
      const r = hero.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      W = r.width;
      H = r.height;
      cv.width = W * dpr;
      cv.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = Math.round(Math.min(90, (W * H) / 9000));
      pts = Array.from({ length: n }, () => {
        const x = Math.random() * W;
        const y = Math.random() * H;
        return { x, y, hx: x, hy: y, ha: Math.random() * 6.28, hs: 0.002 + Math.random() * 0.004, vx: 0, vy: 0, r: Math.random() * 1.4 + 0.5, e: 0 };
      });
    };
    const readColor = () => {
      const probe = colorProbeRef.current;
      const m = probe && getComputedStyle(probe).color.match(/[\d.]+/g);
      if (m) col = m.slice(0, 3).map(Number);
    };
    const frame = () => {
      if (frameN++ % 20 === 0) readColor();
      ctx.clearRect(0, 0, W, H);
      const [r, g, b] = col;
      waves = waves.filter((w) => (w.r += w.big ? 9 : 6) < Math.max(W, H) * 0.8);
      for (const p of pts) {
        const mx = p.x - mouse.x;
        const my = p.y - mouse.y;
        const md = Math.hypot(mx, my);
        if (md < 120 && md > 0) {
          p.vx += (mx / md) * 0.04;
          p.vy += (my / md) * 0.04;
        }
        for (const w of waves) {
          const dx = p.x - w.x;
          const dy = p.y - w.y;
          const d = Math.hypot(dx, dy);
          if (Math.abs(d - w.r) < 18 && d > 0) {
            const f = w.big ? 1.4 : 0.9;
            p.vx += (dx / d) * f;
            p.vy += (dy / d) * f;
            p.e = 1;
          }
        }
        p.ha += p.hs;
        const tx = p.hx + Math.cos(p.ha) * 14;
        const ty = p.hy + Math.sin(p.ha * 1.3) * 10;
        p.vx += (tx - p.x) * 0.004;
        p.vy += (ty - p.y) * 0.004;
        p.vx *= 0.92;
        p.vy *= 0.92;
        p.x += p.vx;
        p.y += p.vy;
        p.e *= 0.96;
      }
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        for (let j = i + 1; j < pts.length; j++) {
          const c = pts[j];
          const d = Math.hypot(a.x - c.x, a.y - c.y);
          if (d < 110) {
            ctx.strokeStyle = `rgba(${r},${g},${b},${(1 - d / 110) * (0.18 + Math.max(a.e, c.e) * 0.5)})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(c.x, c.y);
            ctx.stroke();
          }
        }
        ctx.fillStyle = `rgba(${r},${g},${b},${0.35 + a.e * 0.65})`;
        ctx.beginPath();
        ctx.arc(a.x, a.y, a.r + a.e * 1.8, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(frame);
    };

    const onMove = (e: PointerEvent) => {
      const r = hero.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
    };
    const onLeave = () => {
      mouse.x = -999;
      mouse.y = -999;
    };
    const ro = new ResizeObserver(resize);
    ro.observe(hero);
    hero.addEventListener('pointermove', onMove);
    hero.addEventListener('pointerleave', onLeave);
    resize();
    raf = requestAnimationFrame(frame);
    api.current = { pulse: (x, y, big = false) => waves.push({ x, y, r: 0, big }) };
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      hero.removeEventListener('pointermove', onMove);
      hero.removeEventListener('pointerleave', onLeave);
      api.current = { pulse: () => {} };
    };
  }, [canvasRef, heroRef, colorProbeRef]);
  return api;
}

interface WorkflowCommandBarProps {
  projectId: string;
  projectName?: string;
  lifecycle: ProjectLifecycle;
  status?: string;
  isIngesting?: boolean;
}

/**
 * Workspace chrome — Aurora hero with breadcrumb, project title, the
 * Discover → Understand → Operate stepper, and the phase section tabs.
 */
const WorkflowCommandBar: React.FC<WorkflowCommandBarProps> = ({
  projectId,
  projectName,
  lifecycle,
  isIngesting,
}) => {
  const location = useLocation();
  const activeActivity = detectActivityFromPath(location.pathname);
  const phase = activeActivity ?? phaseForPath(location.pathname);
  const exp = phase ? PHASE_EXPERIENCE[phase] : null;
  const phaseIdx = PROJECT_ACTIVITIES.findIndex((a) => a.id === activeActivity);
  const name = projectName ?? '…';
  const indexState = isIngesting ? 'Indexing' : lifecycle.hasIngestedFiles ? 'Ready' : 'Not started';
  const indexTone = isIngesting ? 'var(--wf-understand)' : lifecycle.hasIngestedFiles ? 'var(--wf-ok)' : 'var(--wf-faint)';
  const baselineLocked = lifecycle.phase0HandoffAccepted;

  // Entrance choreography plays once per mount.
  const [intro, setIntro] = useState(true);
  useEffect(() => {
    const t = window.setTimeout(() => setIntro(false), 1600);
    return () => window.clearTimeout(t);
  }, []);

  // Flash the baseline chip when the baseline becomes locked while mounted.
  const [baselineFlash, setBaselineFlash] = useState(false);
  const prevLocked = useRef(baselineLocked);
  useEffect(() => {
    if (baselineLocked && !prevLocked.current) setBaselineFlash(true);
    prevLocked.current = baselineLocked;
  }, [baselineLocked]);

  // Cursor spotlight — written straight to CSS vars to avoid re-renders.
  const heroRef = useRef<HTMLElement>(null);
  const netRef = useRef<HTMLCanvasElement>(null);
  const washRef = useRef<HTMLDivElement>(null);
  const phaseChipRef = useRef<HTMLSpanElement>(null);
  const net = useConstellation(netRef, heroRef, phaseChipRef);

  // Phase change: colour wash + shockwave expanding from the newly active card.
  const prevPhase = useRef(activeActivity);
  useEffect(() => {
    if (prevPhase.current === activeActivity) return;
    prevPhase.current = activeActivity;
    const hero = heroRef.current;
    const wash = washRef.current;
    const card = hero?.querySelector('.wf-step.active .wf-step-btn');
    if (!hero || !wash || !card) return;
    const hr = hero.getBoundingClientRect();
    const br = card.getBoundingClientRect();
    const x = br.left + br.width / 2 - hr.left;
    const y = br.top + br.height / 2 - hr.top;
    wash.style.setProperty('--wx', `${x}px`);
    wash.style.setProperty('--wy', `${y}px`);
    wash.classList.remove('go');
    void wash.offsetWidth;
    wash.classList.add('go');
    net.current.pulse(x, y);
  }, [activeActivity, net]);

  // Baseline lock: big shockwave through the constellation.
  useEffect(() => {
    if (baselineFlash) net.current.pulse(heroRef.current ? heroRef.current.clientWidth / 2 : 0, 0, true);
  }, [baselineFlash, net]);

  const onPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const el = heroRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  return (
    <div
      className={`wf-aurora v3-workflow-command-bar project-workspace-header${intro ? ' intro' : ''}`}
      style={
        {
          '--wf-c': phase ? `var(--wf-${phase})` : 'var(--wf-g2)',
          '--wf-c2': phase ? `var(--wf-${phase}-2)` : 'var(--wf-g1)',
        } as React.CSSProperties
      }
    >
      <section className="wf-hero" aria-labelledby="wf-title" ref={heroRef} onPointerMove={onPointerMove}>
        <div className="wf-aura" aria-hidden="true">
          <span className="a1" />
          <span className="a2" />
          <span className="a3" />
        </div>
        <canvas className="wf-net" ref={netRef} aria-hidden="true" />
        <div className="wf-wash" ref={washRef} aria-hidden="true" />
        <div className="wf-top">
          <div className="wf-id">
            <nav className="wf-eyebrow" aria-label="Breadcrumb">
              <RouterLink to="/projects/all">Projects</RouterLink>
              <span className="sl">/</span>
              <RouterLink to={`/projects/${projectId}`}>{name}</RouterLink>
              {phaseIdx >= 0 && (
                <span className="phase-n" ref={phaseChipRef}>
                  <i />
                  Phase {phaseIdx + 1} of {PROJECT_ACTIVITIES.length}
                </span>
              )}
            </nav>
            <h1 className="wf-title" id="wf-title">
              {exp && <ScrambleVerb key={exp.id} text={exp.verb} />}{' '}
              {name}
            </h1>
          </div>
          <div className="wf-meta">
            <span
              className={`wf-chip${lifecycle.hasIngestedFiles && !isIngesting ? ' live' : ''}`}
              style={{ '--d': indexTone } as React.CSSProperties}>
              <i />
              <b>Index</b>
              {indexState}
            </span>
            <span
              className={`wf-chip${baselineFlash ? ' flash' : ''}`}
              onAnimationEnd={(e) => e.target === e.currentTarget && setBaselineFlash(false)}
              style={{ '--d': baselineLocked ? 'var(--wf-ok)' : 'var(--wf-faint)' } as React.CSSProperties}
            >
              <i />
              <b>Baseline</b>
              {baselineLocked ? 'Locked' : 'Not locked'}
            </span>
          </div>
        </div>
        <ProjectActivityRail projectId={projectId} lifecycle={lifecycle} />
      </section>

      <div className="wf-tabs project-workspace-header__subnav">
        <div className="wf-tabs-nav">
          <PhaseContextNav />
        </div>
        {activeActivity === 'discover' && <BaselineLockButton projectId={projectId} />}
      </div>
    </div>
  );
};

export default WorkflowCommandBar;
